import { useAudioTrackContext } from 'components/app/audio-track/_mediator/audio-track.provider';
import { useWsSeek } from 'components/app/audio-track/_mediator/use-ws-seek';
import { useDraggerContext } from 'components/app/_mediator/app-dragger.provider';
import { useZoomContext } from './zoom.provider';
import { EnumDragger } from '../../app.slice';

const SEEK_ACTIVATION_DELAY = 120;
const ZOOM_SENSITIVITY = 150;

export const useZoomTouch = () => {
  const { setZoom, handleZoom } = useZoomContext();
  const { wsGetCursorFraction } = useAudioTrackContext();
  const { dragger } = useDraggerContext();
  const seekTo = useWsSeek();

  return (element: React.RefObject<HTMLDivElement>) => {
    const activePointers = new Map<number, PointerEvent>();
    let gesture: 'none' | 'pan' | 'zoom' = 'none';
    let timer: ReturnType<typeof setTimeout>;
    let startDistance: number | null = null;

    const start = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      activePointers.set(e.pointerId, e);
      e.preventDefault();
      e.stopPropagation();
      if (activePointers.size === 1) {
        timer = setTimeout(() => {
          if (gesture === 'none' && activePointers.size === 1) {
            gesture = 'pan';
            dragger(element, EnumDragger.ws).start(e, (e) => seekTo(wsGetCursorFraction(e.clientX)));
          }
        }, SEEK_ACTIVATION_DELAY);
      } else if (activePointers.size === 2) {
        if (timer) clearTimeout(timer);
        gesture = 'zoom';
        const [a, b] = Array.from(activePointers.values());
        startDistance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
      }
    };

    const move = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      e.preventDefault();
      if (!activePointers.has(e.pointerId)) return;
      activePointers.set(e.pointerId, e);
      if (gesture === 'zoom' && activePointers.size === 2) {
        if (!startDistance) return;
        const [a, b] = Array.from(activePointers.values());
        const newDistance = Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
        const step = (startDistance - newDistance) / ZOOM_SENSITIVITY;
        startDistance = newDistance;
        setZoom(step);
        handleZoom();
      }
    };

    const end = (e: PointerEvent) => {
      if (e.pointerType !== 'touch') return;
      e.preventDefault();
      activePointers.delete(e.pointerId);
      if (activePointers.size === 0) {
        if (gesture === 'pan') dragger(element, EnumDragger.ws).end(e);
        else if (gesture === 'none') {
          dragger(element, EnumDragger.ws).start(e, (e) => seekTo(wsGetCursorFraction(e.clientX)));
          dragger(element, EnumDragger.ws).end(e);
        }
        gesture = 'none';
        startDistance = null;
        if (timer) clearTimeout(timer);
      }
    };

    return { start, move, end };
  };
};
